import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, X, Shield } from 'lucide-react';
import { driversAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PPEExpiryBanner = () => {
  const location = useLocation();
  const envPrefix = location.pathname.startsWith('/test') ? '/test' : '';
  const { isSupervisor, isAdmin } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPPEExpiries = async () => {
      if (!isSupervisor && !isAdmin) {
        setLoading(false);
        return;
      }

      try {
        const response = await driversAPI.getPPEExpiryBanners();
        setDrivers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch PPE expiry data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPPEExpiries();
  }, [isSupervisor, isAdmin]);

  if (loading || dismissed || drivers.length === 0 || (!isSupervisor && !isAdmin)) {
    return null;
  }

  const criticalCount = drivers.filter(d => d.urgency === 'critical').length;
  const highCount = drivers.filter(d => d.urgency === 'high').length;

  const getBannerColor = () => {
    if (criticalCount > 0) return 'bg-red-600';
    if (highCount > 0) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const getMessage = () => {
    if (criticalCount > 0) {
      return `${criticalCount} driver(s) have PPE expiring TODAY or overdue!`;
    }
    if (highCount > 0) {
      return `${highCount} driver(s) have PPE expiring within 7 days`;
    }
    return `${drivers.length} driver(s) have PPE expiring soon`;
  };

  return (
    <div className={`${getBannerColor()} text-white px-4 py-2`} data-testid="ppe-expiry-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5" />
          <span className="font-medium">{getMessage()}</span>
          <Link 
            to={`${envPrefix}/drivers`}
            className="underline hover:no-underline font-semibold ml-2"
          >
            View Drivers
          </Link>
        </div>
        <button 
          onClick={() => setDismissed(true)}
          className="hover:bg-white/20 rounded p-1"
          aria-label="Dismiss banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PPEExpiryBanner;
